import { t, type TFunction } from "i18next";
import { z } from "zod";
import { usePortServicesFormStore } from "../store/usePortServicesForm.store";
import { validateBunkeringServices } from "./validators/bunkering.validator";
import { validateDebunkeringServices } from "./validators/debunkering.validator";
import { validateDeSlopingServices } from "./validators/deSloping.validator";
import { validatePortServicesDocument } from "./validators/portServicesDocument.validator";
import { validateRts } from "./validators/sharedValidators";
import { validateShipChandlingServices } from "./validators/shipChandling.validator";
import { validateSlopsServices } from "./validators/slops.validator";
import { validateSludgeServices } from "./validators/sludge.validators";
import { validateStsServices } from "./validators/sts.validator";
import { validateWasteDisposalServices } from "./validators/wasteDisposal.validator";

const LANG_BASE = "vessel-call.port-services";
export const PORT_SERVICE_DESCRIPTION_MAX_CHARS = 4000;
export const PORT_SERVICE_REMARKS_MAX_CHARS = 1000;

export const DOCUMENTS_TYPE = {
  CERTIFICATE: "CERTIFICATE",
  PERMIT: "PERMIT",
  DOCUMENT: "DOCUMENT",
  INSURANCE: "INSURANCE",
  PRE_SAILING_MASTER_DECLARATION: "PRE_SAILING_MASTER_DECLARATION",
  VESSEL_MASTER_SOF: "VESSEL_MASTER_SOF",
  OTHER_SPECIFY: "OTHER_SPECIFY",
} as const;

type PortServicesSchemaOptions = {
  currentVesselImoNumber?: string;
};

const required = (max: number, label = t("form-validation.field")) =>
  z
    .string()
    .min(1, t("form-validation.required", { label }))
    .max(max, t("form-validation.max-length", { label, max }));

const documentArraySchema = z
  .array(
    z.object({
      url: z.string().url(),
      type: z.string().optional(),
      certificateType: z.string().optional(),
      documentType: z.string().optional(),
      otherSpecifyType: z
        .string()
        .max(
          30,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.fields.other-specify`),
            max: 30,
          }),
        )
        .optional(),
      documentSummary: z
        .string()
        .max(
          300,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.fields.document-summary`),
            max: 300,
          }),
        )
        .optional(),
      name: z.string().optional(),
      originalFileName: z.string().optional(),
      expirationDate: z.union([z.date(), z.string(), z.null()]).optional(),
      issueDate: z.union([z.date(), z.string(), z.null()]).optional(),
      inspectionDate: z.union([z.date(), z.string(), z.null()]).optional(),
    }),
  )
  .optional();

export const getPortServicesFormSchema = (t: TFunction, options?: PortServicesSchemaOptions) =>
  z
    .object({
      serviceCategory: required(100, t(`${LANG_BASE}.fields.service-category`)),
      serviceType: z.string().optional(),
      direction: z.string().optional(),
      numberOfPackagesHandled: z.string().optional(),
      additionalResources: z
        .array(
          z.object({
            resourceType: z.string().optional(),
            resourceTypeLabel: z.string().optional(),
            numberOfResources: z.string().optional(),
          }),
        )
        .optional(),
      receivingVesselImoNumber: z.string().optional(),
      receivingVesselId: z.string().optional(),
      receivingVesselName: z.string().optional(),
      receivingVesselCallId: z.string().optional(),
      receivingVesselCallNumber: z.string().optional(),
      receivingVesselLookupPending: z.boolean().optional(),
      customsDocuments: documentArraySchema,
      registeredAgentName: z.string().optional(),
      registeredAgentContactPerson: z.string().optional(),
      serviceProviderName: z.string().optional(),
      operationLocation: z.string().optional(),
      intendedOperationDetails: z.string().optional(),
      otherVesselImoNumber: z.string().optional(),
      otherVesselId: z.string().optional(),
      otherVesselName: z.string().optional(),
      otherVesselCallId: z.string().optional(),
      otherVesselCallNumber: z.string().optional(),
      sireReportIssueDate: z.union([z.date(), z.string(), z.null()]).optional(),
      hoseInspectionDate: z.union([z.date(), z.string(), z.null()]).optional(),
      sireReportDocuments: documentArraySchema,
      billOfLadingDocuments: documentArraySchema,
      cargoManifestDocuments: documentArraySchema,
      certificateOfOriginDocuments: documentArraySchema,
      hoseCertificateDocuments: documentArraySchema,
      otherDocuments: documentArraySchema,
      wasteResiduesForDischargeFacility: z
        .array(
          z.object({
            marpolAnnexIOil: z.string().max(100),
            otherSpecifyDetails: z.string().max(250),
            quantityM3: z.number().positive(),
          }),
        )
        .superRefine((wastes, ctx) => {
          wastes.forEach((waste, index) => {
            if (waste.marpolAnnexIOil === "Other (Specify)" && !waste.otherSpecifyDetails.trim()) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: t("form-validation.required", {
                  label: t(`${LANG_BASE}.fields.wasteResiduesForDischargeFacility`),
                }),
                path: ["wasteResiduesForDischargeFacility", index, "otherSpecifyDetails"],
              });
            }
          });
        }),

      serviceSubType: z.string().optional(),
      quantity: z.string().optional(),
      unitOfMeasurement: z.string().optional(),
      numberOfSkips: z.string().optional(),
      garbageCategory: z.string().optional(),
      serviceLocation: z.string().optional(),
      typeOfSludge: z.string().optional(),
      rts: z.union([z.date(), z.string(), z.null()]).optional(),
      description: z
        .string()
        .max(
          PORT_SERVICE_DESCRIPTION_MAX_CHARS,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.fields.service-description`),
            max: PORT_SERVICE_DESCRIPTION_MAX_CHARS,
          }),
        )
        .optional(),
      remarks: z
        .string()
        .max(
          PORT_SERVICE_REMARKS_MAX_CHARS,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.fields.remarks`),
            max: PORT_SERVICE_REMARKS_MAX_CHARS,
          }),
        )
        .optional(),
      attachmentDocuments: documentArraySchema,
      garbageDocument: documentArraySchema,
      slopsDocument: documentArraySchema,
      sludgeDocument: documentArraySchema,
      debunkeringDocument: documentArraySchema,
      newChatMessage: z.string().optional(),
      // bunkering
      bunkeringType: z.string().optional(),
      bunkeringCompanyName: z.string().optional(),
      bunkerBargeIMO: z.string().optional(),
      bunkerBargeName: z.string().optional(),
      bunkerBargeVesselCallId: z.string().optional(),
      bunkerBargeLookupPending: z.boolean().optional(),
      bunkerBargeLookupErrorMessageKey: z.string().optional(),
      // de-sloping
      deSlopingType: z.string().optional(),
      deSlopingCompanyName: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      const { isSubtypeMandatory, isTypeMandatory } = usePortServicesFormStore.getState();
      if (isTypeMandatory && !data.serviceType?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.service-type`),
          }),
          path: ["serviceType"],
        });
      }
      if (isSubtypeMandatory && !data.serviceSubType?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t("form-validation.required", {
            label: t(`${LANG_BASE}.fields.service-subtype`),
          }),
          path: ["serviceSubType"],
        });
      }

      validatePortServicesDocument(data.attachmentDocuments, ctx, t);

      switch (data.serviceCategory) {
        case "Ship to Ship":
          validateStsServices(data, ctx, t, options?.currentVesselImoNumber);
          break;

        case "Ship Chandling":
          validateShipChandlingServices(data, ctx, t);
          break;

        case "Waste Disposal":
          validateWasteDisposalServices(data, ctx, t);
          if (data.serviceType === "Slops") {
            validateSlopsServices(data, ctx, t);
          }
          break;

        case "HSE":
          if (data.serviceType === "Waste Disposal") {
            validateWasteDisposalServices(data, ctx, t);
            if (data.serviceSubType === "Sludge") {
              validateSludgeServices(data, ctx, t);
            }
            if (data.serviceSubType === "Slops") {
              validateSlopsServices(data, ctx, t);
            }
            if (data.serviceSubType === "Sanitation") {
              validateRts(data.rts, ctx, t);
            }
          } else {
            validateRts(data.rts, ctx, t);
          }
          break;

        case "Bunkering":
          validateBunkeringServices(data, ctx, t, options?.currentVesselImoNumber);
          break;

        case "De-bunkering":
          validateDebunkeringServices(data, ctx, t, options?.currentVesselImoNumber);
          break;

        case "De-sloping":
          validateDeSlopingServices(data, ctx, t, options?.currentVesselImoNumber);
          break;

        case "Other Services":
          validateRts(data.rts, ctx, t);
          break;

        default:
          validateRts(data.rts, ctx, t);
          break;
      }
    });

export type PortServicesFormData = z.infer<ReturnType<typeof getPortServicesFormSchema>>;

export const getWasteFormSchema = (tFn: TFunction) =>
  z
    .object({
      marpolAnnexIOil: z
        .string()
        .min(
          1,
          tFn("form-validation.required", {
            label: t(`vessel-call.marpol-annex1.sidebar.fields.marpolAnnexIOil`),
          }),
        )
        .max(100),
      otherSpecifyDetails: z.string().max(
        250,
        tFn("form-validation.max-length", {
          label: tFn("vessel-call.marpol-annex1.sidebar.fields.otherSpecify"),
          max: 250,
        }),
      ),
      quantityM3: z.number().positive(),
    })
    .superRefine((data, ctx) => {
      if (data.marpolAnnexIOil !== "Other (Specify)") return;
      if (data.otherSpecifyDetails.trim()) return;

      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: tFn("form-validation.required", {
          label: tFn(`vessel-call.marpol-annex1.sidebar.fields.otherSpecify`),
        }),
        path: ["otherSpecifyDetails"],
      });
    });

export type WasteFormSchema = z.infer<ReturnType<typeof getWasteFormSchema>>;
